import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import useAuth from '../hooks/useAuth';
import api from '../api/api';
import styles from './ManagementPage.module.css';
import {
  KeyRound, Eye, EyeOff, Check, ShieldCheck, Building2,
  Users, UserPlus, Plus, Trash2, RotateCcw, Eye as EyeIcon,
  Pencil, X, AlertTriangle, ChevronRight, User, Hash,
  Calendar, Briefcase, Lock, Unlock, Loader2, Info,
  UserCog, BarChart3, Mail, ShieldAlert, CheckCircle2,
  XCircle, FileText, Search, Scale, FileBadge, Layers
} from 'lucide-react';

// Import du validateur de mots de passe
import PasswordRequirements, { isPasswordFullyValid } from '../components/PasswordRequirements';

// ─────────────────────────────────────────────────────────────
// DONNÉES MODULES
// ─────────────────────────────────────────────────────────────
const MODULE_DEFINITIONS = [
  { id: 'fiche_poste', label: 'Fiche de Poste',     Icon: FileText,  desc: 'Notes → fiche professionnelle' },
  { id: 'audit_cv',    label: 'Audit CV',           Icon: Search,    desc: 'Analyse de cohérence'          },
  { id: 'matching',    label: 'Matching CV/AO',     Icon: Scale,     desc: 'Score de pertinence'           },
  { id: 'generator',   label: 'Générateur',         Icon: FileBadge, desc: 'Dossier de compétences'        },
  { id: 'job_search',  label: "Recherche d'Offres", Icon: Briefcase, desc: 'Trouver des missions'          },
  { id: 'private_jobs',label: "Offres Privées",     Icon: Lock,      desc: 'Accès aux missions confidentielles' },
];

// ─────────────────────────────────────────────────────────────
// COMPOSANTS UTILITAIRES
// ─────────────────────────────────────────────────────────────
const InputField = ({ label, type = 'text', name, value, onChange, placeholder, required, min, defaultValue, showToggle, isVisible, onToggle }) => (
  <div className={styles.formGroup}>
    <label className={styles.label}>{label}</label>
    <div className={styles.inputWrapper}>
      <input
        type={showToggle ? (isVisible ? 'text' : 'password') : type}
        className={styles.input}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        min={min}
        defaultValue={defaultValue}
      />
      {showToggle && (
        <button type="button" className={styles.eyeBtn} onClick={onToggle} tabIndex={-1}>
          {isVisible ? <EyeOff size={16} strokeWidth={2} /> : <Eye size={16} strokeWidth={2} />}
        </button>
      )}
    </div>
  </div>
);

const Alert = ({ type, message }) => {
  const config = {
    success: { Icon: CheckCircle2, cls: styles.alertSuccess },
    error:   { Icon: XCircle,      cls: styles.alertError   },
  };
  const { Icon, cls } = config[type];
  return (
    <div className={`${styles.alert} ${cls}`}>
      <Icon size={16} strokeWidth={2} />
      <span>{message}</span>
    </div>
  );
};

const Badge = ({ children, variant = 'primary' }) => (
  <span className={`${styles.badge} ${styles[`badge${variant.charAt(0).toUpperCase() + variant.slice(1)}`]}`}>
    {children}
  </span>
);

// ─────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────
const ManagementPage = () => {
  const { user: rawUser } = useAuth();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const hasFetchedInitialData = useRef(false);

  const [permissionsData, setPermissionsData] = useState({
    fiche_poste: false, audit_cv: false, matching: false,
    generator: false, job_search: false, private_jobs: false
  });
  const [isSavingPermissions, setIsSavingPermissions] = useState(false);

  const user = useMemo(() => {
    if (typeof rawUser === 'string') {
      try { return JSON.parse(rawUser); } catch { return null; }
    }
    return rawUser;
  }, [rawUser]);

  const [passwordData, setPasswordData]     = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [passwordError, setPasswordError]   = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [visibility, setVisibility]         = useState({ old: false, new: false, confirm: false, client: false, commercial: false, reset: false });
  const [modalState, setModalState]         = useState({ type: null, item: null });
  
  // États contrôlés pour les créations / réinitialisations
  const [newPasswordReset, setNewPasswordReset] = useState('');
  const [newClientPwd, setNewClientPwd]         = useState('');
  const [newCommercialPwd, setNewCommercialPwd] = useState('');

  const [detailsData, setDetailsData]       = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [editQuotaValue, setEditQuotaValue] = useState(5);

  const fetchData = useCallback(async () => {
    if (user?.type === 'Commercial') { setLoading(false); return; }
    setLoading(true); setError('');
    try {
      const res = await api.get('/management/info');
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Accès non autorisé ou erreur.');
      setData(null);
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => {
    if (user?.id && !hasFetchedInitialData.current) {
      hasFetchedInitialData.current = true;
      fetchData();
    } else if (rawUser !== undefined && user === null && !hasFetchedInitialData.current) {
      setError('Session invalide ou utilisateur non connecté.');
      setLoading(false);
      hasFetchedInitialData.current = true;
    }
  }, [user, rawUser, fetchData]);

  const triggerRefetch = useCallback(() => fetchData(), [fetchData]);
  const toggleVisibility = (key) => setVisibility(prev => ({ ...prev, [key]: !prev[key] }));

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError(''); setPasswordSuccess('');
    if (!isPasswordFullyValid(passwordData.new_password)) return setPasswordError('Le mot de passe ne respecte pas les critères de sécurité.');
    if (passwordData.new_password !== passwordData.confirm_password) return setPasswordError('Les mots de passe ne correspondent pas.');
    
    try {
      const res = await api.put('/users/password', { old_password: passwordData.old_password, new_password: passwordData.new_password });
      setPasswordSuccess(res.data.message || 'Mot de passe modifié avec succès !');
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
    } catch (err) { setPasswordError(err.response?.data?.error || 'Erreur lors de la modification.'); }
  };

  const handleCreateUser = async (e, userType) => {
    e.preventDefault();
    const email = e.target.email.value, prenom = e.target.prenom.value;
    const password = userType === 'client' ? newClientPwd : newCommercialPwd;
    
    const payload = { email, password, prenom };
    if (userType === 'client') payload.max_commerciaux = parseInt(e.target.max_commerciaux.value, 10);
    
    const endpoint = userType === 'client' ? '/management/clients' : '/management/commerciaux';
    
    try {
      await api.post(endpoint, payload);
      e.target.reset();
      // On réinitialise nos états
      if (userType === 'client') setNewClientPwd('');
      if (userType === 'commercial') setNewCommercialPwd('');
      
      setVisibility(prev => ({ ...prev, [userType]: false }));
      triggerRefetch();
    } catch (err) { alert(err.response?.data?.error || 'Erreur lors de la création.'); }
  };

  const handleConfirmDelete = async () => {
    const { type, item } = modalState;
    if (!item) return;
    const endpoint = type === 'deleteCommercial' ? `/management/commerciaux/${item.id}` : `/management/clients/${item.id}`;
    try { await api.delete(endpoint); closeModal(); triggerRefetch(); }
    catch (err) { alert(err.response?.data?.error || 'Erreur lors de la suppression.'); closeModal(); }
  };

  const handleSaveQuota = async () => {
    const { item } = modalState;
    if (!item) return;
    try {
      await api.put(`/management/clients/${item.id}/quota`, { max_commerciaux: parseInt(editQuotaValue, 10) });
      closeModal(); triggerRefetch();
    } catch (err) { alert(err.response?.data?.error || 'Erreur lors de la mise à jour du quota.'); }
  };

  const closeModal = () => {
    setModalState({ type: null, item: null });
    setNewPasswordReset(''); 
    setDetailsData(null);
    setDetailsLoading(false); 
    setIsSavingPermissions(false);
    setVisibility(prev => ({ ...prev, reset: false }));
  };

  const openDeleteModal      = (item, type) => setModalState({ type, item });
  const openResetModal        = (u) => setModalState({ type: 'resetPassword', item: u });
  const openEditQuotaModal = (u) => { setEditQuotaValue(u.max_commerciaux ?? 5); setModalState({ type: 'editQuota', item: u }); };
  const openPermissionsModal  = (u) => {
    setPermissionsData({
      fiche_poste: u.permissions?.fiche_poste ?? true,
      audit_cv: u.permissions?.audit_cv ?? true,
      matching: u.permissions?.matching ?? true,
      generator: u.permissions?.generator ?? true,
      job_search: u.permissions?.job_search ?? true,
      private_jobs: u.permissions?.private_jobs ?? false, // <--- NOUVEAU (false par défaut)
    });
    setModalState({ type: 'permissions', item: u });
  };

  const openViewDetailsModal = async (u) => {
    setModalState({ type: 'viewDetails', item: u });
    setDetailsLoading(true);
    try {
      const res = await api.get(`/management/clients/${u.id}/details`);
      setDetailsData(res.data);
    } catch (err) { setDetailsData({ error: err.response?.data?.error || 'Erreur de chargement.' }); }
    finally { setDetailsLoading(false); }
  };

  const handleConfirmReset = async () => {
    const { item } = modalState;
    if (!item || !isPasswordFullyValid(newPasswordReset)) { alert('Le mot de passe ne respecte pas les critères de sécurité.'); return; }
    try { await api.put(`/management/users/${item.id}/password-reset`, { new_password: newPasswordReset }); closeModal(); }
    catch (err) { alert(err.response?.data?.error || 'Erreur lors de la réinitialisation.'); }
  };

  const handlePermissionToggle = (key) => setPermissionsData(prev => ({ ...prev, [key]: !prev[key] }));

  const handleSavePermissions = async () => {
    const { item } = modalState;
    if (!item) return;
    setIsSavingPermissions(true);
    try { await api.put(`/management/users/${item.id}/permissions`, { permissions: permissionsData }); closeModal(); triggerRefetch(); }
    catch (err) { alert(err.response?.data?.error || 'Erreur lors de la mise à jour des permissions.'); }
    finally { setIsSavingPermissions(false); }
  };

  const canGrant = (permKey) => {
    if (user?.type === 'Admin') return true;
    if (user?.type === 'Client') return user?.permissions?.[permKey] === true;
    return false;
  };

  // ── États de chargement / erreur ────────────────────────
  if (loading || user === undefined) {
    return (
      <div className={styles.stateContainer}>
        <div className={styles.loaderCard}>
          <Loader2 size={36} className={styles.spinnerIcon} />
          <p className={styles.stateText}>Chargement des données…</p>
        </div>
      </div>
    );
  }

  if ((user === null || error) && user?.type !== 'Commercial') {
    return (
      <div className={styles.stateContainer}>
        <div className={styles.errorCard}>
          <div className={styles.errorIconWrap}><ShieldAlert size={32} /></div>
          <h2 className={styles.stateTitle}>Erreur d'accès</h2>
          <p className={styles.stateText}>{error || 'Session invalide. Veuillez vous reconnecter.'}</p>
        </div>
      </div>
    );
  }

  if (!data && user?.type !== 'Commercial') {
    return (
      <div className={styles.stateContainer}>
        <div className={styles.errorCard}>
          <div className={styles.errorIconWrap}><Info size={32} /></div>
          <h2 className={styles.stateTitle}>Aucune donnée</h2>
          <p className={styles.stateText}>Aucune donnée de gestion à afficher.</p>
        </div>
      </div>
    );
  }

  const commercialCount       = user?.type === 'Client' && data?.commerciaux ? data.commerciaux.length : 0;
  const maxCommerciauxDisplay = user?.max_commerciaux ?? 5;
  const quotaPercent          = Math.min((commercialCount / maxCommerciauxDisplay) * 100, 100);
  const isQuotaFull           = commercialCount >= maxCommerciauxDisplay;

  // ── Helper liste utilisateurs ────────────────────────────
  const renderUserList = (users, typeLabel, deleteType) => (
    <div className={styles.userList}>
      {users.length > 0 ? users.map(u => (
        <div key={u.id} className={styles.userRow}>
          <div className={styles.userAvatar}>
            {(u.email?.[0] ?? '?').toUpperCase()}
          </div>
          <div className={styles.userInfo}>
            <span className={styles.userEmail}>{u.email}</span>
            <div className={styles.userMeta}>
              {typeLabel === 'Client' && (
                <span className={styles.metaChip}>
                    <Users size={11} strokeWidth={2} />
                    Max {u.max_commerciaux ?? 5} commerciaux
                </span>
              )}
              {typeLabel === 'Commercial' && (
                <span className={styles.metaChip}>
                  <BarChart3 size={11} strokeWidth={2} />
                  Quota : {u.quota}
                </span>
              )}
              <span className={styles.metaChip}>
                <Calendar size={11} strokeWidth={2} />
                {u.date_creation}
              </span>
            </div>
          </div>
          <div className={styles.userActions}>
            <ActionBtn onClick={() => openPermissionsModal(u)} title="Gérer les accès" variant="neutral">
              <ShieldCheck size={15} strokeWidth={2} />
            </ActionBtn>
            {typeLabel === 'Client' && (
              <>
                <ActionBtn onClick={() => openEditQuotaModal(u)} title="Modifier le quota" variant="neutral">
                  <Pencil size={15} strokeWidth={2} />
                </ActionBtn>
                <ActionBtn onClick={() => openViewDetailsModal(u)} title="Voir les détails" variant="info">
                  <EyeIcon size={15} strokeWidth={2} />
                </ActionBtn>
              </>
            )}
            <ActionBtn onClick={() => openResetModal(u)} title="Réinitialiser le mot de passe" variant="warning">
              <RotateCcw size={15} strokeWidth={2} />
            </ActionBtn>
            <ActionBtn onClick={() => openDeleteModal(u, deleteType)} title="Supprimer" variant="danger">
              <Trash2 size={15} strokeWidth={2} />
            </ActionBtn>
          </div>
        </div>
      )) : (
        <div className={styles.emptyState}>
          <Users size={32} strokeWidth={1.5} className={styles.emptyIcon} />
          <p>Aucun utilisateur pour le moment.</p>
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.page}>

      {/* ── En-tête de page ── */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderInner}>
          <div className={styles.pageHeaderIcon}>
            <BarChart3 size={22} strokeWidth={2} />
          </div>
          <div>
            <h1 className={styles.pageTitle}>Pilotage</h1>
            <p className={styles.pageSubtitle}>
              Gérez vos utilisateurs, permissions et paramètres de sécurité.
            </p>
          </div>
        </div>
      </div>

      {/* ── Grille principale ── */}
      <div className={styles.mainGrid}>

        {/* ── Carte : Changer mot de passe ── */}
        <section className={`${styles.card} ${styles.cardPassword}`}>
          <div className={styles.cardHead}>
            <div className={styles.cardHeadIcon} data-variant="primary">
              <KeyRound size={18} strokeWidth={2} />
            </div>
            <div>
              <h2 className={styles.cardTitle}>Sécurité du compte</h2>
              <p className={styles.cardDesc}>Modifiez votre mot de passe de connexion.</p>
            </div>
          </div>

          <form onSubmit={handleChangePassword} className={styles.form}>
            <div className={styles.formRow}>
              <InputField
                label="Ancien mot de passe"
                name="old_password"
                value={passwordData.old_password}
                onChange={e => setPasswordData({ ...passwordData, old_password: e.target.value })}
                placeholder="••••••••"
                required
                showToggle isVisible={visibility.old} onToggle={() => toggleVisibility('old')}
              />
              
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <InputField
                  label="Nouveau mot de passe"
                  name="new_password"
                  value={passwordData.new_password}
                  onChange={e => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  placeholder="••••••••"
                  required
                  showToggle isVisible={visibility.new} onToggle={() => toggleVisibility('new')}
                />
                {passwordData.new_password && (
                  <div style={{ marginTop: '-8px', marginBottom: '16px' }}>
                    <PasswordRequirements password={passwordData.new_password} />
                  </div>
                )}
              </div>

              <InputField
                label="Confirmer"
                name="confirm_password"
                value={passwordData.confirm_password}
                onChange={e => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                placeholder="••••••••"
                required
                showToggle isVisible={visibility.confirm} onToggle={() => toggleVisibility('confirm')}
              />
            </div>

            {passwordError   && <Alert type="error"   message={passwordError}   />}
            {passwordSuccess && <Alert type="success"  message={passwordSuccess} />}

            <div className={styles.formActions}>
              <button 
                type="submit" 
                className={`${styles.btn} ${styles.btnPrimary}`}
                disabled={!isPasswordFullyValid(passwordData.new_password) || passwordData.new_password !== passwordData.confirm_password}
                style={{ opacity: (!isPasswordFullyValid(passwordData.new_password) || passwordData.new_password !== passwordData.confirm_password) ? 0.6 : 1, cursor: (!isPasswordFullyValid(passwordData.new_password) || passwordData.new_password !== passwordData.confirm_password) ? 'not-allowed' : 'pointer' }}
              >
                <Check size={16} strokeWidth={2.5} />
                Mettre à jour
              </button>
            </div>
          </form>
        </section>

        {/* ── VUE ADMIN ── */}
        {user?.type === 'Admin' && data?.clients && (
          <>
            {/* Créer un client */}
            <section className={styles.card}>
              <div className={styles.cardHead}>
                <div className={styles.cardHeadIcon} data-variant="success">
                  <Building2 size={18} strokeWidth={2} />
                </div>
                <div>
                  <h2 className={styles.cardTitle}>Nouveau client</h2>
                  <p className={styles.cardDesc}>Créer un compte client sur la plateforme.</p>
                </div>
              </div>
              <form onSubmit={e => handleCreateUser(e, 'client')} className={styles.form}>
                <InputField label="Adresse email"      type="email"  name="email"            placeholder="client@exemple.com"  required />
                <InputField label="Prénom"             type="text"   name="prenom"           placeholder="Jean"                required />
                <InputField label="Max commerciaux" type="number" name="max_commerciaux" defaultValue={5} min="0" required />
                
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <InputField
                    label="Mot de passe"
                    name="password"
                    value={newClientPwd}
                    onChange={(e) => setNewClientPwd(e.target.value)}
                    placeholder="••••••••" 
                    required
                    showToggle isVisible={visibility.client} onToggle={() => toggleVisibility('client')}
                  />
                  {newClientPwd && (
                    <div style={{ marginTop: '-8px', marginBottom: '16px' }}>
                      <PasswordRequirements password={newClientPwd} />
                    </div>
                  )}
                </div>

                <div className={styles.formActions}>
                  <button 
                    type="submit" 
                    className={`${styles.btn} ${styles.btnPrimary}`}
                    disabled={!isPasswordFullyValid(newClientPwd)}
                    style={{ opacity: (!isPasswordFullyValid(newClientPwd)) ? 0.6 : 1, cursor: (!isPasswordFullyValid(newClientPwd)) ? 'not-allowed' : 'pointer' }}
                  >
                    <Plus size={16} strokeWidth={2.5} />
                    Créer le client
                  </button>
                </div>
              </form>
            </section>

            {/* Liste clients */}
            <section className={styles.card}>
              <div className={styles.cardHead}>
                <div className={styles.cardHeadIcon} data-variant="info">
                  <Layers size={18} strokeWidth={2} />
                </div>
                <div className={styles.cardHeadFlex}>
                  <div>
                    <h2 className={styles.cardTitle}>Clients</h2>
                    <p className={styles.cardDesc}>Tous les comptes clients enregistrés.</p>
                  </div>
                  <Badge variant="primary">{data.clients.length}</Badge>
                </div>
              </div>
              {renderUserList(data.clients, 'Client', 'deleteClient')}
            </section>
          </>
        )}

        {/* ── VUE CLIENT ── */}
        {user?.type === 'Client' && data?.commerciaux && (
          <>
            {/* Quota + Créer commercial */}
            <section className={styles.card}>
              <div className={styles.cardHead}>
                <div className={styles.cardHeadIcon} data-variant="success">
                  <UserPlus size={18} strokeWidth={2} />
                </div>
                <div>
                  <h2 className={styles.cardTitle}>Nouveau commercial</h2>
                  <p className={styles.cardDesc}>Ajouter un commercial à votre équipe.</p>
                </div>
              </div>

              {/* Barre de quota */}
              <div className={styles.quotaBar}>
                <div className={styles.quotaBarHeader}>
                  <span className={styles.quotaLabel}>
                    <Users size={14} strokeWidth={2} />
                    Commerciaux
                  </span>
                  <span className={`${styles.quotaCount} ${isQuotaFull ? styles.quotaFull : ''}`}>
                    {commercialCount} / {maxCommerciauxDisplay}
                  </span>
                </div>
                <div className={styles.quotaTrack}>
                  <div
                    className={`${styles.quotaFill} ${isQuotaFull ? styles.quotaFillFull : ''}`}
                    style={{ width: `${quotaPercent}%` }}
                  />
                </div>
              </div>

              <form onSubmit={e => handleCreateUser(e, 'commercial')} className={styles.form}>
                <InputField label="Adresse email" type="email" name="email"  placeholder="commercial@exemple.com" required />
                <InputField label="Prénom"        type="text"  name="prenom" placeholder="Marie"                required />
                
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <InputField
                    label="Mot de passe"
                    name="password" 
                    value={newCommercialPwd}
                    onChange={(e) => setNewCommercialPwd(e.target.value)}
                    placeholder="••••••••" 
                    required
                    showToggle isVisible={visibility.commercial} onToggle={() => toggleVisibility('commercial')}
                  />
                  {newCommercialPwd && (
                    <div style={{ marginTop: '-8px', marginBottom: '16px' }}>
                      <PasswordRequirements password={newCommercialPwd} />
                    </div>
                  )}
                </div>

                <div className={styles.formActions}>
                  <button
                    type="submit"
                    className={`${styles.btn} ${styles.btnPrimary}`}
                    disabled={isQuotaFull || !isPasswordFullyValid(newCommercialPwd)}
                    style={{ opacity: (isQuotaFull || !isPasswordFullyValid(newCommercialPwd)) ? 0.6 : 1, cursor: (isQuotaFull || !isPasswordFullyValid(newCommercialPwd)) ? 'not-allowed' : 'pointer' }}
                  >
                    <Plus size={16} strokeWidth={2.5} />
                    Créer le commercial
                  </button>
                </div>
              </form>
            </section>

            {/* Liste commerciaux */}
            <section className={styles.card}>
              <div className={styles.cardHead}>
                <div className={styles.cardHeadIcon} data-variant="info">
                  <Users size={18} strokeWidth={2} />
                </div>
                <div className={styles.cardHeadFlex}>
                  <div>
                    <h2 className={styles.cardTitle}>Mon équipe</h2>
                    <p className={styles.cardDesc}>Vos commerciaux actifs sur la plateforme.</p>
                  </div>
                  <Badge variant="primary">{data.commerciaux.length}</Badge>
                </div>
              </div>
              {renderUserList(data.commerciaux, 'Commercial', 'deleteCommercial')}
            </section>
          </>
        )}
      </div>

      {/* ══════════════════════════════════════════════
          MODALS
      ══════════════════════════════════════════════ */}

      {/* Modal : Permissions */}
      {modalState.type === 'permissions' && (
        <Modal onClose={closeModal}>
          <ModalHeader
            Icon={ShieldCheck}
            iconVariant="primary"
            title="Gestion des accès"
            subtitle={modalState.item?.email}
            onClose={closeModal}
          />
          <div className={styles.modalBody}>
            <p className={styles.modalIntro}>
              Définissez les modules accessibles pour cet utilisateur.
            </p>
            <div className={styles.permissionList}>
              {MODULE_DEFINITIONS.map(({ id, label, Icon: ModIcon, desc }) => {
                const disabled = !canGrant(id);
                const checked  = permissionsData[id];
                return (
                  <div
                    key={id}
                    className={`${styles.permRow} ${disabled ? styles.permDisabled : ''} ${checked ? styles.permChecked : ''}`}
                    onClick={() => !disabled && handlePermissionToggle(id)}
                  >
                    <div className={styles.permIcon}>
                      <ModIcon size={16} strokeWidth={1.75} />
                    </div>
                    <div className={styles.permInfo}>
                      <span className={styles.permLabel}>{label}</span>
                      <span className={styles.permDesc}>{desc}</span>
                    </div>
                    <div className={styles.permToggleWrap}>
                      {disabled ? (
                        <span className={styles.permLockedChip}><Lock size={11} /> Non acquis</span>
                      ) : (
                        <div className={`${styles.toggle} ${checked ? styles.toggleOn : ''}`}>
                          <div className={styles.toggleThumb} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className={styles.modalFooter}>
            <button className={`${styles.btn} ${styles.btnGhost}`} onClick={closeModal} disabled={isSavingPermissions}>
              Annuler
            </button>
            <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleSavePermissions} disabled={isSavingPermissions}>
              {isSavingPermissions ? <Loader2 size={15} className={styles.spinAnim} /> : <Check size={15} strokeWidth={2.5} />}
              {isSavingPermissions ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal : Edit Quota */}
      {modalState.type === 'editQuota' && (
        <Modal onClose={closeModal}>
          <ModalHeader
            Icon={Pencil}
            iconVariant="primary"
            title="Modifier le quota"
            subtitle={modalState.item?.email}
            onClose={closeModal}
          />
          <div className={styles.modalBody}>
            <InputField
              label="Nombre maximum de commerciaux"
              type="number"
              value={editQuotaValue}
              onChange={e => setEditQuotaValue(e.target.value)}
              min="0"
            />
          </div>
          <div className={styles.modalFooter}>
            <button className={`${styles.btn} ${styles.btnGhost}`}   onClick={closeModal}>Annuler</button>
            <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleSaveQuota}>
              <Check size={15} strokeWidth={2.5} />
              Sauvegarder
            </button>
          </div>
        </Modal>
      )}

      {/* Modal : Supprimer */}
      {(modalState.type === 'deleteClient' || modalState.type === 'deleteCommercial') && (
        <Modal onClose={closeModal} size="sm">
          <ModalHeader
            Icon={AlertTriangle}
            iconVariant="danger"
            title="Confirmer la suppression"
            onClose={closeModal}
          />
          <div className={styles.modalBody}>
            <div className={styles.dangerZone}>
              <div className={styles.dangerIconWrap}>
                <AlertTriangle size={28} strokeWidth={1.75} />
              </div>
              <p className={styles.dangerMsg}>
                Vous allez supprimer le compte de<br />
                <strong>{modalState.item?.email}</strong>
              </p>
              <p className={styles.dangerWarning}>Cette action est irréversible.</p>
            </div>
          </div>
          <div className={styles.modalFooter}>
            <button className={`${styles.btn} ${styles.btnGhost}`}  onClick={closeModal}>Annuler</button>
            <button className={`${styles.btn} ${styles.btnDanger}`} onClick={handleConfirmDelete}>
              <Trash2 size={15} strokeWidth={2} />
              Supprimer
            </button>
          </div>
        </Modal>
      )}

      {/* Modal : Reset Password */}
      {modalState.type === 'resetPassword' && (
        <Modal onClose={closeModal} size="sm">
          <ModalHeader
            Icon={RotateCcw}
            iconVariant="warning"
            title="Réinitialiser le mot de passe"
            subtitle={modalState.item?.email}
            onClose={closeModal}
          />
          <div className={styles.modalBody}>
            <InputField
              label="Nouveau mot de passe"
              placeholder="••••••••"
              value={newPasswordReset}
              onChange={e => setNewPasswordReset(e.target.value)}
              showToggle isVisible={visibility.reset} onToggle={() => toggleVisibility('reset')}
            />
            {/* CHECKLIST DE SÉCURITÉ */}
            {newPasswordReset && (
              <div style={{ marginTop: '8px' }}>
                <PasswordRequirements password={newPasswordReset} />
              </div>
            )}
          </div>
          <div className={styles.modalFooter}>
            <button className={`${styles.btn} ${styles.btnGhost}`}   onClick={closeModal}>Annuler</button>
            <button 
              className={`${styles.btn} ${styles.btnWarning}`} 
              onClick={handleConfirmReset}
              disabled={!isPasswordFullyValid(newPasswordReset)}
              style={{ opacity: !isPasswordFullyValid(newPasswordReset) ? 0.6 : 1, cursor: !isPasswordFullyValid(newPasswordReset) ? 'not-allowed' : 'pointer' }}
            >
              <RotateCcw size={15} strokeWidth={2} />
              Réinitialiser
            </button>
          </div>
        </Modal>
      )}

      {/* Modal : Détails client */}
      {modalState.type === 'viewDetails' && (
        <Modal onClose={closeModal} size="lg">
          <ModalHeader
            Icon={EyeIcon}
            iconVariant="info"
            title="Détails du client"
            subtitle={modalState.item?.email}
            onClose={closeModal}
          />
          <div className={styles.modalBody}>
            {detailsLoading ? (
              <div className={styles.modalLoader}>
                <Loader2 size={28} className={styles.spinAnim} />
                <p>Chargement des détails…</p>
              </div>
            ) : detailsData?.error ? (
              <Alert type="error" message={detailsData.error} />
            ) : detailsData ? (
              <div className={styles.detailsWrap}>
                {/* Info client */}
                <div className={styles.clientCard}>
                  <div className={styles.clientAvatar}>
                    {(detailsData.client?.email?.[0] ?? '?').toUpperCase()}
                  </div>
                  <div className={styles.clientInfo}>
                    <span className={styles.clientEmail}>{detailsData.client?.email}</span>
                    <div className={styles.clientMeta}>
                      <span><Hash size={11} /> {detailsData.client?.id}</span>
                      <span><BarChart3 size={11} /> Quota : {detailsData.client?.quota ?? 'N/A'}</span>
                      <span><Calendar size={11} /> {detailsData.client?.date_creation}</span>
                    </div>
                  </div>
                </div>

                <div className={styles.detailsDivider} />

                {/* Liste commerciaux */}
                <div className={styles.detailsSection}>
                  <div className={styles.detailsSectionHead}>
                    <Users size={16} strokeWidth={2} />
                    <h4>Commerciaux associés</h4>
                    <Badge variant="neutral">{detailsData.commerciaux?.length || 0}</Badge>
                  </div>
                  {detailsData.commerciaux?.length > 0 ? (
                    <div className={styles.detailsUserList}>
                      {detailsData.commerciaux.map(c => (
                        <div key={c.id} className={styles.detailsUserRow}>
                          <div className={styles.detailsUserAvatar}>
                            {(c.email?.[0] ?? '?').toUpperCase()}
                          </div>
                          <div className={styles.detailsUserInfo}>
                            <span className={styles.detailsUserEmail}>{c.email}</span>
                            <div className={styles.detailsUserMeta}>
                              <span><BarChart3 size={11} /> Quota : {c.quota}</span>
                              <span><Calendar size={11} /> {c.date_creation}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={styles.emptyText}>Aucun commercial associé.</p>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────
const ActionBtn = ({ onClick, title, variant = 'neutral', children }) => (
  <button className={`${styles.actionBtn} ${styles[`actionBtn${variant.charAt(0).toUpperCase() + variant.slice(1)}`]}`}
    onClick={onClick} title={title} type="button">
    {children}
  </button>
);

const Modal = ({ children, onClose, size = 'md' }) => (
  <>
    <div className={styles.backdrop} onClick={onClose} />
    <div className={styles.modalWrap}>
      <div className={`${styles.modal} ${styles[`modal${size.charAt(0).toUpperCase() + size.slice(1)}`]}`}>
        {children}
      </div>
    </div>
  </>
);

const ModalHeader = ({ Icon, iconVariant = 'primary', title, subtitle, onClose }) => (
  <div className={styles.modalHead}>
    <div className={styles.modalHeadLeft}>
      <div className={`${styles.modalHeadIcon}`} data-variant={iconVariant}>
        <Icon size={18} strokeWidth={2} />
      </div>
      <div>
        <h3 className={styles.modalTitle}>{title}</h3>
        {subtitle && <p className={styles.modalSubtitle}>{subtitle}</p>}
      </div>
    </div>
    <button className={styles.closeBtn} onClick={onClose} type="button">
      <X size={18} strokeWidth={2} />
    </button>
  </div>
);

export default ManagementPage;